import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { acceptInvite } from '../api';
import { useAuth } from '../auth';

export function InvitePage() {
  const { user, loading, enabled, sessionError, retrySession, signIn } = useAuth();
  const [params] = useSearchParams();
  const code = useMemo(() => (params.get('code') ?? '').trim(), [params]);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const returnTo = code ? `/invite?code=${encodeURIComponent(code)}` : '/invite';

  async function onSignIn() {
    setBusy(true);
    setError(null);
    try {
      await signIn(returnTo);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed.');
      setBusy(false);
    }
  }

  async function onContinue() {
    if (!code) {
      setError('This invitation is missing.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await acceptInvite(code);
      navigate(`/packproofs/${result.transactionId}`, { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'This invitation is not available.');
      setBusy(false);
    }
  }

  if (loading) return <main className="main"><p className="meta">Starting secure session…</p></main>;

  if (sessionError && !user) {
    return (
      <main className="sign-in card">
        <p className="eyebrow brand"><img src="/brand/packproof-icon.png" alt="" width="28" height="28" />PackProof</p>
        <h1>Secure session unavailable.</h1>
        <p className="error">{sessionError}</p>
        <div className="row">
          <button className="btn" type="button" disabled={!enabled} onClick={retrySession}>Try again</button>
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="sign-in card">
        <p className="eyebrow brand"><img src="/brand/packproof-icon.png" alt="" width="28" height="28" />PackProof</p>
        <h1>You've received a PackProof.</h1>
        <p className="lede">Sign in to continue.</p>
        {error ? <p className="error">{error}</p> : null}
        <div className="row">
          <button className="btn" type="button" disabled={busy || !enabled} onClick={() => void onSignIn()}>Sign in to continue</button>
        </div>
      </main>
    );
  }

  return (
    <main className="sign-in card">
      <p className="eyebrow brand"><img src="/brand/packproof-icon.png" alt="" width="28" height="28" />PackProof</p>
      <h1>You've received a PackProof.</h1>
      <p className="lede">Continue to review the item and price.</p>
      {error ? <p className="error">{error}</p> : null}
      <div className="row">
        <button className="btn" type="button" disabled={busy || !code} onClick={() => void onContinue()}>Continue</button>
      </div>
    </main>
  );
}
